/**
 * AutoFilter: сортировка + галочки значений + условие (>, ≥, <, ≤, =, ≠, содержит).
 */
import { displayValue, rawCell } from './formulaEngine.js';

export const FILTER_OPS = [
  { id: 'eq', label: 'Равно' },
  { id: 'ne', label: 'Не равно' },
  { id: 'contains', label: 'Содержит' },
  { id: 'gt', label: 'Больше' },
  { id: 'gte', label: 'Больше или равно' },
  { id: 'lt', label: 'Меньше' },
  { id: 'lte', label: 'Меньше или равно' },
];

export function emptyFilterState() {
  return { enabled: false, columns: {} };
}

export function normalizeFilterState(raw) {
  if (!raw || typeof raw !== 'object') return emptyFilterState();
  const columns = {};
  const src = raw.columns && typeof raw.columns === 'object' ? raw.columns : raw;
  for (const [k, v] of Object.entries(src)) {
    if (k === 'enabled') continue;
    if (!v || typeof v !== 'object') continue;
    const col = {
      sort: v.sort === 'asc' || v.sort === 'desc' ? v.sort : null,
      selected: Array.isArray(v.selected) ? v.selected.map(String) : null,
      condition: null,
    };
    if (v.condition && typeof v.condition === 'object' && v.condition.op) {
      col.condition = {
        op: String(v.condition.op),
        value: String(v.condition.value ?? ''),
      };
    }
    columns[String(k)] = col;
  }
  return {
    enabled: Boolean(raw.enabled),
    columns,
  };
}

function cellDisplay(state, r, c, evalCell) {
  if (typeof evalCell === 'function') {
    return String(displayValue(state, r, c, evalCell) ?? '');
  }
  return String(rawCell(state, r, c) ?? '');
}

function toNum(s) {
  const t = String(s ?? '').trim().replace(',', '.');
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

export function matchCondition(display, condition) {
  if (!condition || !condition.op) return true;
  const op = condition.op;
  const want = String(condition.value ?? '');
  const text = String(display ?? '');
  if (op === 'contains') return text.toLowerCase().includes(want.toLowerCase());
  if (op === 'eq') return text === want;
  if (op === 'ne') return text !== want;
  const a = toNum(text);
  const b = toNum(want);
  if (a == null || b == null) return false;
  if (op === 'gt') return a > b;
  if (op === 'gte') return a >= b;
  if (op === 'lt') return a < b;
  if (op === 'lte') return a <= b;
  return true;
}

export function uniqueColumnValues(state, col, evalCell) {
  const rows = state.rows || 0;
  const set = new Set();
  for (let r = 0; r < rows; r += 1) {
    set.add(cellDisplay(state, r, col, evalCell));
  }
  return Array.from(set).sort((a, b) => {
    const na = toNum(a);
    const nb = toNum(b);
    if (na != null && nb != null) return na - nb;
    return String(a).localeCompare(String(b), 'ru');
  });
}

/**
 * Какие строки видны (массив индексов по возрастанию).
 * Сортировка: если есть sort на столбце — переупорядочиваем visible.
 */
export function computeVisibleRows(state, filter, evalCell) {
  const rows = state.rows || 0;
  const f = normalizeFilterState(filter);
  if (!f.enabled) {
    return Array.from({ length: rows }, (_, i) => i);
  }

  let indices = [];
  for (let r = 0; r < rows; r += 1) {
    let ok = true;
    for (const [ck, conf] of Object.entries(f.columns)) {
      const c = Number(ck);
      if (!Number.isFinite(c)) continue;
      const disp = cellDisplay(state, r, c, evalCell);
      if (conf.selected) {
        if (!conf.selected.includes(disp)) {
          ok = false;
          break;
        }
      }
      if (conf.condition && !matchCondition(disp, conf.condition)) {
        ok = false;
        break;
      }
    }
    if (ok) indices.push(r);
  }

  // одна активная сортировка — первый столбец с sort
  let sortCol = null;
  let sortDir = null;
  for (const [ck, conf] of Object.entries(f.columns)) {
    if (conf.sort === 'asc' || conf.sort === 'desc') {
      sortCol = Number(ck);
      sortDir = conf.sort;
      break;
    }
  }
  if (sortCol != null && Number.isFinite(sortCol)) {
    indices = indices.slice().sort((ra, rb) => {
      const va = cellDisplay(state, ra, sortCol, evalCell);
      const vb = cellDisplay(state, rb, sortCol, evalCell);
      const na = toNum(va);
      const nb = toNum(vb);
      let cmp;
      if (na != null && nb != null) cmp = na - nb;
      else cmp = String(va).localeCompare(String(vb), 'ru');
      return sortDir === 'desc' ? -cmp : cmp;
    });
  }

  return indices;
}

export function setFilterEnabled(state, enabled) {
  const filter = normalizeFilterState(state.filter);
  return { ...state, filter: { ...filter, enabled: Boolean(enabled) } };
}

export function setColumnFilter(state, col, patch) {
  const filter = normalizeFilterState(state.filter);
  const key = String(col);
  const prev = filter.columns[key] || { sort: null, selected: null, condition: null };
  const next = { ...prev, ...patch };
  const columns = { ...filter.columns, [key]: next };
  return {
    ...state,
    filter: { enabled: true, columns },
  };
}

export function clearColumnFilter(state, col) {
  const filter = normalizeFilterState(state.filter);
  const columns = { ...filter.columns };
  delete columns[String(col)];
  return { ...state, filter: { ...filter, columns } };
}
